// ═══════════════════════════════════════════════════════════════════════════════
// Task assignee helpers — multi-assignee normalization
//
// Tasks migrated from single-assignee (`assigneeId: string`) to multi-assignee
// (`assigneeIds: string[]`).  Old records may only carry the scalar field, so
// every reader funnels through `getTaskAssigneeIds` to normalize the shape.
// Writers should populate both fields so legacy consumers keep working until
// the back-compat field is removed.
// ═══════════════════════════════════════════════════════════════════════════════
import type { Task } from '../types';

type TaskLike = Pick<Task, 'assigneeId' | 'assigneeIds'>;

/** Always returns an array. Empty when the task has no assignee at all. */
export function getTaskAssigneeIds(task: TaskLike | null | undefined): string[] {
  if (!task) return [];
  if (Array.isArray(task.assigneeIds) && task.assigneeIds.length > 0) {
    return task.assigneeIds;
  }
  return task.assigneeId ? [task.assigneeId] : [];
}

/** First assignee, or empty string when unassigned — for legacy callers that
 *  expect a scalar (Avatar component, etc.). */
export function getPrimaryAssigneeId(task: TaskLike | null | undefined): string {
  return getTaskAssigneeIds(task)[0] || '';
}

/** Membership test that handles both the array and legacy scalar shape. */
export function isAssignedTo(task: TaskLike | null | undefined, userId: string): boolean {
  if (!userId) return false;
  return getTaskAssigneeIds(task).includes(userId);
}

/** Returns the canonical write shape: keeps `assigneeIds` authoritative and
 *  mirrors `assigneeIds[0]` into `assigneeId` for back-compat. */
export function normalizeAssigneeFields(
  ids: string[],
  fallbackUserId?: string,
): { assigneeId: string; assigneeIds: string[] } {
  const cleaned = Array.from(new Set(ids.filter(Boolean)));
  if (cleaned.length === 0 && fallbackUserId) cleaned.push(fallbackUserId);
  return {
    assigneeId: cleaned[0] || '',
    assigneeIds: cleaned,
  };
}
